import { useEffect, useRef } from 'react';
import Router from 'next/router';
import { getOrCreateVisitorId } from '../../utils/visitorId';
import {
  buildUnitGroups,
  unitIdsOfGroup,
  type UnitGroupConfig,
  type UnitGroupingMode,
} from '@autodromo/units';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
    __afVisitorId?: string | null;
  }
}

interface CentralAnalyticsProps {
  metadata: {
    channelPublicId?: string | null;
    accountId?: string | null;
    brandPublicId?: string | null;
    disableCentralAnalytics?: boolean;
    units?: Array<{ id: string; publicId?: string; state?: string | null }>;
    unitGroupingMode?: UnitGroupingMode | null;
    unitGroups?: UnitGroupConfig[] | null;
  } | null;
  channelData: {
    id?: string;
    publicId?: string;
    brandPublicId?: string | null;
    units?: Array<{ id: string; publicId?: string; state?: string | null }>;
    unitGroupingMode?: UnitGroupingMode | null;
    unitGroups?: UnitGroupConfig[] | null;
  } | null;
}

const CENTRAL_GA4_ID = process.env.NEXT_PUBLIC_CENTRAL_GA4_ID || '';
const PREVIEW_DOMAIN_SUFFIX = process.env.NEXT_PUBLIC_PREVIEW_DOMAIN_SUFFIX || '';

function getSelectedUnitPublicId(
  metadata: CentralAnalyticsProps['metadata'],
  channelData: CentralAnalyticsProps['channelData']
): string | null {
  const units = metadata?.units || channelData?.units || [];

  // Se tem 1 unidade, usar o publicId
  if (units.length === 1) {
    return units[0].publicId || null;
  }

  if (units.length <= 1) return null;

  try {
    // O UnitSelectionModal salva o id interno — mapear para publicId
    const selectedUnit = localStorage.getItem('selectedUnit');
    if (selectedUnit) {
      const unit = units.find((u) => u.id === selectedUnit);
      return unit?.publicId || null;
    }

    // Com agrupamento o visitante escolhe uma região, nunca uma unidade. A
    // dimensão recebe a PRIMEIRA unidade do grupo, na ordem em que o site a
    // exibe.
    const selectedGroup = localStorage.getItem('selectedUnitGroup');
    if (!selectedGroup) return null;

    const groups = buildUnitGroups({
      units,
      mode: metadata?.unitGroupingMode ?? channelData?.unitGroupingMode,
      groups: metadata?.unitGroups ?? channelData?.unitGroups,
    });

    const [firstUnitId] = unitIdsOfGroup(groups, selectedGroup);
    if (!firstUnitId) return null;

    return units.find((u) => u.id === firstUnitId)?.publicId || null;
  } catch {
    // localStorage indisponível
    return null;
  }
}

function isPreviewOrLocal(): boolean {
  const hostname = window.location.hostname;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return true;
  }

  if (PREVIEW_DOMAIN_SUFFIX && hostname.endsWith(PREVIEW_DOMAIN_SUFFIX)) {
    return true;
  }

  return false;
}

export const CentralAnalytics = ({ metadata, channelData }: CentralAnalyticsProps) => {
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    if (!CENTRAL_GA4_ID) return;
    if (typeof window === 'undefined') return;
    if (metadata?.disableCentralAnalytics) return;
    if (isPreviewOrLocal()) return;

    initialized.current = true;

    const visitorId = getOrCreateVisitorId();

    // Disponibilizar globalmente para custom scripts
    window.__afVisitorId = visitorId;

    // Garantir que gtag existe
    if (!window.gtag) {
      window.dataLayer = window.dataLayer || [];
      window.gtag = function () {
        window.dataLayer!.push(arguments);
      };
    }

    const siteId = channelData?.publicId || metadata?.channelPublicId || null;
    const accountId = metadata?.accountId || null;
    const brandId = metadata?.brandPublicId || channelData?.brandPublicId || null;
    const unitId = getSelectedUnitPublicId(metadata, channelData);

    // Configurar conta central com user_properties inline (isolamento do visitor_id)
    window.gtag('config', CENTRAL_GA4_ID, {
      send_page_view: false,
      site_id: siteId,
      account_id: accountId,
      brand_id: brandId,
      unit_id: unitId,
      ...(visitorId ? { user_properties: { af_visitor_id: visitorId } } : {}),
    });

    // Primeiro page_view
    window.gtag('event', 'page_view', {
      send_to: CENTRAL_GA4_ID,
    });
  }, [metadata, channelData]);

  // Listener de navegação SPA
  useEffect(() => {
    if (!CENTRAL_GA4_ID) return;

    const handleRouteChange = (url: string) => {
      if (!window.gtag) return;
      window.gtag('event', 'page_view', {
        send_to: CENTRAL_GA4_ID,
        page_path: url,
        unit_id: getSelectedUnitPublicId(metadata, channelData),
      });
    };

    Router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      Router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, []);

  return null;
};
